import { InvariantViolation } from '@shared-kernel/domain-error.js';

/**
 * SSOT for party-size-tiered pricing. Replaces the TWO previous defs:
 *   - booking_core's `PartySizeTier` (field: amountPerPerson)
 *   - play_session's `PartyTier` (field: amountPerPersonPerHour)
 *
 * New canonical field name: `amountPerPersonPerHour` — explicit unit so
 * nobody wonders whether it's per-visit, per-hour, per-month.
 */
export interface PartyTier {
  readonly minPartySize: number;
  /** Inclusive upper bound; null = open-ended (highest tier only). */
  readonly maxPartySize: number | null;
  /** TWD per person per hour for this tier. */
  readonly amountPerPersonPerHour: number;
}

/**
 * Validate + sort tiers for use by TieredPerPersonStrategy. Enforces:
 *   - non-empty
 *   - non-overlapping
 *   - contiguous (no gaps)
 *   - only the last tier may be open-ended
 */
export function validateAndSortTiers(tiers: readonly PartyTier[]): readonly PartyTier[] {
  if (tiers.length === 0) {
    throw new InvariantViolation('PartyTier: tiers must not be empty');
  }
  const sorted = [...tiers].sort((a, b) => a.minPartySize - b.minPartySize);
  for (let i = 1; i < sorted.length; i += 1) {
    const prev = sorted[i - 1]!;
    const cur = sorted[i]!;
    if (prev.maxPartySize === null) {
      throw new InvariantViolation(
        'PartyTier: only the LAST tier may be open-ended',
      );
    }
    if (cur.minPartySize !== prev.maxPartySize + 1) {
      throw new InvariantViolation(
        `PartyTier: tiers must be contiguous; gap between ${prev.maxPartySize} and ${cur.minPartySize}`,
      );
    }
  }
  return sorted;
}

/** Find the tier matching a party size, or null if none matches. */
export function findTierFor(
  tiers: readonly PartyTier[],
  partySize: number,
): PartyTier | null {
  return (
    tiers.find(
      (t) =>
        partySize >= t.minPartySize &&
        (t.maxPartySize === null || partySize <= t.maxPartySize),
    ) ?? null
  );
}
